/**
 * @fileoverview Composes company catalog sub-hooks into a single page hook.
 * @module hooks/useCompanyCatalog
 */
import { use, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { AppContext } from "../context/AppContext";
import { useNavigation } from "../context/NavigationContext";
import { DEFAULT_RESULTS_LIMIT } from "../logic/companyCatalog/index.js";
import { useCompanyCatalogSearch } from "./companyCatalog/useCompanyCatalogSearch.js";
import { useCompanyCatalogPagination } from "./companyCatalog/useCompanyCatalogPagination.js";
import { useCompanyCatalogInit } from "./companyCatalog/useCompanyCatalogInit.js";
import { useCompanyCatalogPermissions } from "./companyCatalog/useCompanyCatalogPermissions.js";
import { useCompanyCatalogControls } from "./companyCatalog/useCompanyCatalogControls.js";
import { buildCompanyCatalogViewModel } from "./companyCatalog/buildCompanyCatalogViewModel.js";

/**
 * Composes catalog sub-hooks into the state and handlers consumed by the CompanyCatalog page.
 *
 * Responsibilities are split across search, pagination, initialization, permissions,
 * toolbar controls, and a pure view-model builder to keep each module testable.
 *
 * @returns {{
 *   sortConfig: { key: string, direction: 'ascending'|'descending' },
 *   searchQuery: string,
 *   loading: boolean,
 *   togglingCompanyId: string|null,
 *   showCreateRequirementsColumn: boolean,
 *   paginatedCompaniesList: Array<Object>,
 *   totalPages: number,
 *   currentPage: number,
 *   limitReached: boolean,
 *   companiesData: Array<Object>,
 *   handleSort: () => void,
 *   updateSearchQuery: (event: import('react').ChangeEvent<HTMLInputElement>) => void,
 *   handleSearch: () => Promise<void>,
 *   handleExportCsv: () => Promise<void>,
 *   handleResultsLimitChange: (selectedOption: { value: number }) => void,
 *   handlePageChange: (direction: 'next'|'prev') => Promise<void>,
 *   handleCreateRequirementsChange: (event: import('react').ChangeEvent<HTMLInputElement>) => void,
 *   handleEditCompanyClick: (event: import('react').MouseEvent<HTMLElement>) => void
 * }} Catalog table state and event handlers for the page component
 */
const useCompanyCatalog = () => {
  const navigate = useNavigate();
  const { changePermissions } = use(AppContext);
  const { updateNavigation } = useNavigation();
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);

  const search = useCompanyCatalogSearch(navigate);
  const pagination = useCompanyCatalogPagination(navigate, resultsLimit);

  const init = useCompanyCatalogInit({
    navigate,
    changePermissions,
    updateNavigation,
    setInitialCompanies: search.setInitialCompanies,
    setLastKey: pagination.setLastKey,
  });

  const permissions = useCompanyCatalogPermissions({
    navigate,
    companiesData: search.companiesData,
    setCompaniesData: search.setCompaniesData,
  });

  const controls = useCompanyCatalogControls({
    navigate,
    companiesData: search.companiesData,
    handleSearch: search.handleSearch,
    resetPagination: pagination.resetPagination,
    setResultsLimit,
    isRfcSearchActive: search.isRfcSearchActive,
    lastKey: pagination.lastKey,
    setExportLoading: pagination.setLoading,
  });

  const onPageChange = useCallback(
    (direction) =>
      pagination.handlePageChange(
        direction,
        search.companiesData,
        search.setCompaniesData,
      ),
    [pagination, search.companiesData, search.setCompaniesData],
  );

  const viewModel = buildCompanyCatalogViewModel({
    companiesData: search.companiesData,
    currentPage: pagination.currentPage,
    resultsLimit,
    lastKey: pagination.lastKey,
    limitReached: pagination.limitReached,
  });

  return {
    sortConfig: controls.sortConfig,
    searchQuery: search.searchQuery,
    loading: init.initialLoading || search.loading || pagination.loading,
    togglingCompanyId: permissions.togglingCompanyId,
    showCreateRequirementsColumn: init.showCreateRequirementsColumn,
    paginatedCompaniesList: viewModel.paginatedCompaniesList,
    totalPages: viewModel.totalPages,
    currentPage: pagination.currentPage,
    limitReached: pagination.limitReached,
    companiesData: search.companiesData,
    handleSort: controls.handleSort,
    updateSearchQuery: search.updateSearchQuery,
    handleSearch: controls.runSearch,
    handleExportCsv: controls.handleExportCsv,
    handleResultsLimitChange: controls.handleResultsLimitChange,
    handlePageChange: onPageChange,
    handleCreateRequirementsChange: permissions.handleCreateRequirementsChange,
    handleEditCompanyClick: controls.handleEditCompanyClick,
  };
};

export default useCompanyCatalog;
